import classNames from 'classnames';
import { Button } from '@carbon/react';
import { Renew } from '@carbon/react/icons';
import { Link } from 'components/link';
import { formatNumber } from 'utils/number';
import type { IntersectedWells } from './use-intersected-wells';
import styles from './create-area-panel.module.scss';

interface IntersectedWellsSectionProps {
  wells: IntersectedWells;
  /** Whether there is a previewed shape to match the wells against. */
  hasShape: boolean;
}

/**
 * A Unit's Intersected wells: the wells inside the previewed boundary, found on
 * request and re-checked from the card.
 *
 * @param {IntersectedWellsSectionProps} props - The match and whether it can run.
 * @return {JSX.Element} The section.
 */
export function IntersectedWellsSection({
  wells,
  hasShape,
}: IntersectedWellsSectionProps): JSX.Element {
  return (
    <section className={classNames(styles.section, styles.divided)}>
      <h4 className={styles.sectionTitle}>Intersected wells</h4>
      {/* The big button only until there are results; from then on the
        card's own Re-check re-runs the match, the way the mock has it. */}
      {wells.totalCount === null && (
        <Button
          kind="tertiary"
          size="md"
          // Matched against the previewed shape, so it needs one to exist.
          disabled={!hasShape || wells.loading}
          onClick={wells.find}
        >
          {wells.loading ? 'Finding wells…' : 'Find intersected wells'}
        </Button>
      )}

      {wells.totalCount === null && !wells.error && (
        <p className={styles.hint}>
          Draw or select the unit boundary first — wells are matched against
          that shape.
        </p>
      )}

      {wells.error && <p className={styles.hint}>{wells.error}</p>}

      {wells.totalCount !== null && (
        <div className={styles.wellsPanel}>
          <div className={styles.wellsHeader}>
            <span className={styles.wellsCount}>
              {formatNumber(wells.totalCount, 0, 0)}{' '}
              {wells.totalCount === 1 ? 'well' : 'wells'}
            </span>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Renew}
              disabled={wells.loading}
              onClick={wells.find}
            >
              {wells.loading ? 'Re-checking…' : 'Re-check'}
            </Button>
          </div>
          {wells.wells.length > 0 && (
            <ul className={styles.wellsList}>
              {wells.wells.map((well) => (
                <li key={well.id} className={styles.wellRow}>
                  {/* The mock's bold "NAME - UWI" row title, kept a link
                    into the well's page. */}
                  <Link to={well.href} className={styles.wellName}>
                    {well.name && well.uwi
                      ? `${well.name} - ${well.uwi}`
                      : well.name || well.uwi}
                  </Link>
                  {(well.status || well.currentOperator) && (
                    <div className={styles.wellMeta}>
                      {well.status && <span>{well.status}</span>}
                      {well.currentOperator && (
                        <span>{well.currentOperator}</span>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {/* The query returns a page; the count above covers the rest. */}
          {wells.totalCount > wells.wells.length && (
            <p className={styles.hint}>
              Showing the first {formatNumber(wells.wells.length, 0, 0)}.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
